import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Award, CalendarClock, CreditCard, FileText, Gift, Wallet, type LucideIcon } from "lucide-react";

import { AwardsPanel, PaymentsPanel, RenewalsPanel } from "../components/scholarship/AwardPanels";
import { ApplicationsPanel, ScholarshipsPanel } from "../components/scholarship/ScholarshipPanels";
import { ugx } from "../components/scholarship/scholarshipLogic";
import { usePermissions } from "../hooks/usePermissions";
import { academicSetupService } from "../services/academicSetupService";
import { scholarshipService } from "../services/scholarshipService";
import { useAuthStore } from "../stores/authStore";

type Tab = "scholarships" | "applications" | "awards" | "disbursements" | "renewals";

/**
 * Scholarships: what the school offers, families' applications and the
 * decisions on them, the awards, what is paid out of each, and renewals.
 * Families see the open scholarships, their own applications and awards.
 */
export default function Scholarships() {
  const { hasCapability } = usePermissions();
  const canManage = hasCapability("access.scholarship.finance");
  const canApply = hasCapability("access.scholarship.applicant");
  const isParent = useAuthStore((state) => state.user?.user_type) === "parent";
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tabs: { id: Tab; label: string; icon: LucideIcon; badge?: number; staff?: boolean }[] = [];
  const view = params.get("view") ?? undefined;
  const [applyingFor, setApplyingFor] = useState<number | null>(null);

  const stats = useQuery({ queryKey: ["scholarship", "stats"], queryFn: scholarshipService.getStats });
  const scholarships = useQuery({ queryKey: ["scholarship", "list"], queryFn: scholarshipService.getScholarships });
  const applications = useQuery({ queryKey: ["scholarship", "applications"], queryFn: scholarshipService.getApplications });
  const awards = useQuery({ queryKey: ["scholarship", "awards"], queryFn: scholarshipService.getAwards });
  const payments = useQuery({ queryKey: ["scholarship", "payments"], queryFn: scholarshipService.getPayments, enabled: canManage });
  const renewals = useQuery({ queryKey: ["scholarship", "renewals"], queryFn: scholarshipService.getRenewals, enabled: canManage });
  const learners = useQuery({ queryKey: ["scholarship", "learners"], queryFn: scholarshipService.getEligibleStudents, enabled: canApply && applyingFor !== null });
  const years = useQuery({ queryKey: ["academic-years"], queryFn: academicSetupService.getYears, enabled: canManage });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["scholarship"] });

  const s = stats.data;
  const dash = (value?: number | string) => (stats.isLoading || value === undefined ? "–" : String(value));
  tabs.push(
    { id: "scholarships", label: "Scholarships", icon: Gift },
    { id: "applications", label: "Applications", icon: FileText, badge: canManage ? s?.open_applications : undefined },
    { id: "awards", label: "Awards", icon: Award },
    { id: "disbursements", label: "Disbursements", icon: CreditCard, staff: true },
    { id: "renewals", label: "Renewals", icon: CalendarClock, badge: s?.pending_renewals, staff: true },
  );
  const shown = tabs.filter((item) => !item.staff || canManage);
  const tab: Tab = shown.find((item) => item.id === params.get("tab"))?.id ?? "scholarships";
  const go = (next: Tab, nextView?: string) => {
    const query: Record<string, string> = {};
    if (next !== "scholarships") query.tab = next;
    if (nextView) query.view = nextView;
    setParams(query, { replace: true });
  };
  const apply = (scholarshipId = 0) => { setApplyingFor(scholarshipId); go("applications"); };

  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = canManage
    ? [
        { label: "Open scholarships", value: dash(s?.active_scholarships), note: "Taking applications now", go: () => go("scholarships"), icon: Gift },
        { label: "Applications to decide", value: dash(s?.open_applications), note: s?.approved_without_award ? `${s.approved_without_award} approved, not yet awarded` : `${s?.applications ?? 0} in all`, go: () => go("applications", s?.open_applications ? "pending" : s?.approved_without_award ? "to_award" : undefined), icon: FileText, alert: Boolean(s?.open_applications || s?.approved_without_award) },
        { label: "Learners on awards", value: dash(s?.active_awards), note: `${s?.scholarship_students ?? 0} have ever held one`, go: () => go("awards"), icon: Award },
        { label: "Left to pay out", value: dash(s ? ugx(s.balance_to_pay) : undefined), note: s ? `${ugx(s.total_paid)} paid so far` : "", go: () => go("awards", "unpaid"), icon: Wallet, alert: Number(s?.balance_to_pay ?? 0) > 0 },
      ]
    : [
        { label: "Open scholarships", value: dash(s?.active_scholarships), note: "You can apply for these", go: () => go("scholarships"), icon: Gift },
        { label: "Your applications", value: dash(s?.applications), note: s?.pending_applications ? `${s.pending_applications} waiting for a decision` : "None waiting", go: () => go("applications"), icon: FileText },
        { label: "Awards", value: dash(s?.awards), note: s ? `${ugx(s.total_awarded)} awarded` : "", go: () => go("awards"), icon: Award },
      ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Scholarships</h1>
        <p className="mt-1 text-sm text-gray-500">{canManage ? "What the school offers, families' applications and the decisions on them, the awards, what has been paid out of each, and renewals." : "Scholarships you can apply for, your applications and the school's decisions, and any awards."}</p>
      </div>

      <div className={`grid gap-3 sm:grid-cols-2 ${tiles.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {tiles.map((tile) => (
          <button key={tile.label} type="button" onClick={tile.go} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Scholarship sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {shown.map((item) => {
          const active = tab === item.id;
          return (
            <button key={item.id} type="button" role="tab" aria-selected={active} onClick={() => go(item.id)} className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}>
              <item.icon className="h-4 w-4" />
              {item.label}
              {item.badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{item.badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "scholarships" && (
        <ScholarshipsPanel rows={scholarships.data} years={years.data ?? []} canManage={canManage} canApply={canApply} isLoading={scholarships.isLoading} isError={scholarships.isError} onRetry={() => scholarships.refetch()} onChanged={refresh} onApply={(row) => apply(row.id)} />
      )}
      {tab === "applications" && (
        <ApplicationsPanel
          rows={applications.data}
          scholarships={scholarships.data ?? []}
          learners={learners.data ?? []}
          canManage={canManage}
          isParent={isParent}
          canApply={canApply}
          isLoading={applications.isLoading}
          isError={applications.isError}
          onRetry={() => applications.refetch()}
          onChanged={refresh}
          initialView={view ?? (canManage ? undefined : "all")}
          applyingFor={learners.isLoading ? null : applyingFor}
          onApply={() => apply()}
          onCloseApply={() => setApplyingFor(null)}
        />
      )}
      {tab === "awards" && (
        <AwardsPanel rows={awards.data} payments={payments.data ?? []} renewals={renewals.data ?? []} canManage={canManage} isLoading={awards.isLoading} isError={awards.isError} onRetry={() => awards.refetch()} onChanged={refresh} initialView={view ?? (canManage ? undefined : "all")} />
      )}
      {tab === "disbursements" && canManage && <PaymentsPanel rows={payments.data} isLoading={payments.isLoading} isError={payments.isError} onRetry={() => payments.refetch()} />}
      {tab === "renewals" && canManage && <RenewalsPanel rows={renewals.data} isLoading={renewals.isLoading} isError={renewals.isError} onRetry={() => renewals.refetch()} onChanged={refresh} />}
    </div>
  );
}
